import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, BookOpen } from 'lucide-react';
import axios from 'axios';
import './HomeBookCard.css';
import SkincareBanner from './SkincareBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sections, setSections] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchHeaderData = async () => {
      try {
        const [sectionsRes, siteInfoRes] = await Promise.all([
          axios.get(`${API_URL}/site-sections`),
          axios.get(`${API_URL}/site-info`)
        ]);
        setSections(sectionsRes.data);
        setSiteInfo(siteInfoRes.data);
      } catch (error) {
        console.error('Error loading header settings:', error);
      }
    };
    fetchHeaderData();
  }, []);

  const navLinks = useMemo(() => {
    const navigation = sections?.navigation || {};
    return [
      { name: 'Home', path: '/', visible: true },
      { name: 'About Lindsey', path: '/about', visible: navigation.about !== false },
      { name: 'My Book', path: '/book', visible: navigation.book !== false && sections?.book?.enabled !== false },
      { name: 'Q&A', path: '/qa', visible: navigation.qa !== false },
      { name: 'Downloads', path: '/downloads', visible: navigation.downloads !== false },
      { name: 'Booking', path: '/booking', visible: navigation.booking !== false && sections?.booking?.enabled !== false },
      { name: 'Blog', path: '/blog', visible: navigation.blog !== false }
    ].filter(link => link.visible);
  }, [sections]);

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-sage-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="text-2xl font-serif text-sage-800">Healing-Women</div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  link.path === '/book'
                    ? 'home-book-nav'
                    : isActive(link.path)
                    ? 'text-sage-800 bg-sage-100'
                    : 'text-gray-700 hover:text-sage-800 hover:bg-sage-50'
                }`}
              >
                {link.path === '/book' && <BookOpen className="h-4 w-4" aria-hidden="true" />}
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Contact Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href={`tel:${siteInfo?.phone || '+16047801466'}`} className="flex items-center space-x-2 text-sage-800 hover:text-sage-900 transition-colors">
              <Phone className="h-4 w-4" />
              <span className="text-sm font-medium">{siteInfo?.phone || '+1 604 780 1466'}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-sage-50 transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {sections && <SkincareBanner settings={sections.skincare} pathname={location.pathname} />}

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-sage-200 bg-white">
          <nav className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  link.path === '/book'
                    ? 'home-book-nav'
                    : isActive(link.path)
                    ? 'text-sage-800 bg-sage-100'
                    : 'text-gray-700 hover:text-sage-800 hover:bg-sage-50'
                }`}
              >
                {link.path === '/book' && <BookOpen className="h-4 w-4" aria-hidden="true" />}
                {link.name}
              </Link>
            ))}
            <a
              href={`tel:${siteInfo?.phone || '+16047801466'}`}
              className="flex items-center space-x-2 px-4 py-3 text-sage-800 hover:bg-sage-50 rounded-lg transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm font-medium">{siteInfo?.phone || '+1 604 780 1466'}</span>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
